// Nomad-mon Go! — v12 FORGED FX: the ONE shared effect layer for Duo/Fusion
// cards in focus (Collection hero, squad-carousel centre card, forge reveal,
// battle cut-in). One fixed-position WebGL canvas follows whichever element is
// attached; nothing else on the page pays for it, and no CSS filter is used.
//
// FUSION (over the top, per tier band: 0 ember, 1 plasma, 2 prismatic):
//   * an animated prismatic energy border with an outer bloom
//   * an energy CORE under the card's foot: a pulsing reactor with plasma tendrils
//   * orbiting reagent SIGILS on a tilted ellipse (they pass behind the card and
//     back over it)
//   * plasma/ember particles rising off the card, and HEAT HAZE: rising
//     refractive ribbons of light above it
//   * a slow pulse of light through the whole card
// DUO: a calmer pink/cyan rim with glints travelling the edge (the Duo's own
//   signature — the split breathing apart — is baked into its video clip).
//
// Compositing: premultiplied output whose colour may exceed its alpha, so
// light ADDS to whatever is under the card (an additive glow) while solid
// strokes still cover.
// Performance: one quad, one fragment shader, canvas sized to the card + a
// margin, DPR capped at 1.5 and dropped live (never persisted) if the layer's
// own frames run long; Android low-memory devices start at the low step; the
// layer sleeps when nothing is attached or the host is hidden; prefers-reduced-
// motion freezes time. No WebGL -> the layer simply does nothing.

const PAD = 0.42;             // canvas margin around the card, x card width
const STEPS = [1.5, 1.1, 0.8]; // DPR cap per quality step
const VS = `attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}`;
const FS = `precision mediump float;
uniform vec2 uRes;uniform vec4 uRect;uniform float uT,uKind,uBand,uTier,uBoost,uQ,uLvl;
float h1(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n2(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(h1(i),h1(i+vec2(1,0)),f.x),mix(h1(i+vec2(0,1)),h1(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float a=.5,s=0.;for(int i=0;i<4;i++){if(float(i)>=2.+2.*uQ)break;s+=a*n2(p);p=p*2.03+7.1;a*=.5;}return s;}
vec3 hue(float h){return clamp(abs(fract(h+vec3(0.,.6667,.3333))*6.-3.)-1.,0.,1.);}
float sdRR(vec2 p,vec2 b,float r){vec2 q=abs(p)-b+r;return length(max(q,0.))+min(max(q.x,q.y),0.)-r;}
vec3 bandCol(float k){ // 0 primary, 1 secondary
  if(uBand<.5)return mix(vec3(1.,.48,.12),vec3(1.,.86,.45),k);
  if(uBand<1.5)return mix(vec3(.55,.32,1.),vec3(.25,.94,1.),k);
  return mix(vec3(1.,.36,.78),vec3(.37,1.,.88),k);}
void main(){
  vec2 px=gl_FragCoord.xy;px.y=uRes.y-px.y;
  vec2 c=uRect.xy+uRect.zw*.5;vec2 hs=uRect.zw*.5;float W=uRect.z;
  vec2 q=px-c;float rad=W*.065;
  float d=sdRR(q,hs,rad);                    // <0 inside the card
  vec2 uv=(px-uRect.xy)/uRect.zw;            // 0..1 card space (y down)
  float t=uT;float boost=uBoost;
  vec3 col=vec3(0.);float a=0.;
  float ang=atan(q.y,q.x);
  if(uKind<.5){ // ---------------- DUO
    float rim=exp(-abs(d)/(W*.012));float glow=exp(-max(d,0.)/(W*.05))*step(0.,d);
    float g1=pow(max(0.,cos(ang*1.-t*1.7)),24.),g2=pow(max(0.,cos(ang*1.+t*1.3+2.)),24.);
    vec3 rc=mix(vec3(1.,.45,.9),vec3(.3,.95,1.),.5+.5*sin(ang*2.+t*.8));
    col+=rc*(rim*(.55+1.6*(g1+g2))+glow*.32*(1.+boost));a=max(a,rim*.9+glow*.4);
    vec2 eg=min(px,uRes-px);float fade=smoothstep(0.,W*.3,min(eg.x,eg.y));
    gl_FragColor=vec4(col,clamp(a*.55,0.,1.))*fade; return;
  }
  // ---------------- FUSION
  float tierK=uTier/24.;float pulse=.5+.5*sin(t*2.1);
  // prismatic border: hue runs round the card and flows in time
  float bw=W*(.012+.006*tierK);
  float rim=exp(-pow(abs(d)/bw,1.6));
  float hh=ang/6.2832+t*(.12+.1*uBand);
  vec3 prism=uBand>1.5?hue(hh):mix(bandCol(0.),bandCol(1.),.5+.5*sin(ang*3.+t*2.5));
  float crawl=.6+.4*sin(ang*14.-t*7.)*sin(ang*5.+t*3.);
  col+=prism*rim*(1.4+crawl)*(1.+boost)*mix(.5,1.,uLvl);a=max(a,rim);
  float bloom=step(0.,d)*exp(-d/(W*(.1+.08*tierK)*uLvl))*(.7+.45*pulse)*(1.+1.5*boost)*uLvl;
  col+=mix(bandCol(0.),prism,.5)*bloom*1.1;a=max(a,bloom*.85);
  // plasma ARCS crackling round the border
  float arcN=n2(vec2(ang*7.+floor(t*9.)*3.1,t*2.));
  float arc=smoothstep(.72-.12*tierK,.9,arcN)*exp(-abs(d)/(W*(.018+.02*tierK)));
  col+=mix(vec3(1.),bandCol(1.),.4)*arc*2.2*uLvl;a=max(a,clamp(arc*1.5,0.,1.));
  // god rays fanning out from behind the card
  float rays=step(0.,d)*pow(abs(sin(ang*7.+t*.35)),26.)*exp(-d/(W*.45*uLvl))*(.35+.65*tierK)*(1.+boost)*uLvl;
  col+=prism*rays*.6;a=max(a,rays*.45);
  // inner pulse of light through the card (kept low so the art reads)
  float inner=step(d,0.)*exp(d/(W*.11))*(.22+.3*pulse)*(1.+boost);
  col+=bandCol(1.)*inner;a=max(a,inner*.5);
  // energy core under the card's foot
  vec2 cc=c+vec2(0.,hs.y*.98);vec2 cq=(px-cc)/W;float cr=length(cq);
  float ca=atan(cq.y,cq.x);
  float tend=fbm(vec2(ca*2.5+t*.6,cr*7.-t*2.4));
  float core=exp(-cr/(.06+.03*pulse+.04*boost))*1.8+exp(-cr/.22)*tend*(.9+.9*tierK);
  core*=(1.+1.4*boost)*uLvl;
  vec3 coreC=mix(bandCol(0.),vec3(1.,.97,.9),clamp(exp(-cr/.03),0.,1.));
  col+=coreC*core;a=max(a,clamp(core,0.,1.)*.95);
  // rising heat haze ribbons above the card
  if(uQ>.3){
    float above=smoothstep(0.,-W*.05,px.y-uRect.y)*smoothstep(-W*.55,-W*.08,px.y-uRect.y);
    float side=smoothstep(hs.x*1.05,hs.x*.5,abs(q.x));
    float hz=fbm(vec2(q.x/W*5.,(px.y/W)*3.+t*1.6));
    float rib=pow(abs(sin((q.x/W)*14.+hz*6.+t*.8)),18.)*above*side;
    col+=mix(prism,vec3(1.),.4)*rib*.7*uLvl;a=max(a,rib*.45*uLvl);
  }
  // orbiting reagent sigils (behind = only outside the card)
  float nS=4.+uBand+floor(uBand*.6);
  for(int i=0;i<7;i++){
    float fi=float(i);if(fi>=nS)break;
    float th=t*(.55+.06*fi)+fi*6.2832/nS;
    float z=sin(th);
    vec2 sp=c+vec2(cos(th)*hs.x*1.42,sin(th)*.22*hs.y-cos(th*.5+fi)*hs.y*.62);
    float sz=W*(.11+.035*z)*mix(.75,1.,uLvl);
    vec2 sq=(px-sp)/sz;float sl=length(sq);
    float ring=exp(-pow(abs(sl-.8)/.09,2.))+exp(-pow(abs(sl-.5)/.06,2.))*.6;
    float rot=atan(sq.y,sq.x)+t*2.+fi;
    float spokes=pow(max(0.,cos(rot*4.)),30.)*smoothstep(.85,.3,sl)*step(.18,sl);
    float dot_=exp(-sl*sl/.03);
    float s=(ring+spokes+dot_*1.5)*smoothstep(1.3,.9,sl);
    float vis=z>0.?(d<0.?.4:1.):step(0.,d)*.55;
    vec3 sc=uBand>1.5?hue(fi/nS+t*.1):bandCol(mod(fi,2.));
    col+=sc*s*vis*(1.2+boost);a=max(a,clamp(s,0.,1.)*vis);
  }
  // plasma / ember particles rising off the card
  float pl=0.;
  for(int L=0;L<3;L++){
    float fl=float(L);if(fl>=1.+2.*uQ)break;
    float cs=W*(.11-.025*fl);
    vec2 g=vec2(px.x,px.y+t*W*(.16+.07*fl))/cs;
    vec2 id=floor(g),f=fract(g)-.5;
    float r=h1(id+fl*17.);
    vec2 o=vec2(h1(id+3.1)-.5,h1(id+9.7)-.5)*.6+vec2(.12*sin(t*2.+r*6.),0.);
    float e=exp(-dot(f-o,f-o)/(.004+.005*r))*step(.5-.22*tierK,r);
    e*=smoothstep(W*.75,W*.1,length(q*vec2(1.,.75)))*(.6+.4*sin(t*6.+r*40.))*(d>0.?1.:.22);
    pl+=e;
  }
  col+=mix(bandCol(1.),vec3(1.),.35)*pl*1.8*mix(.6,1.,uLvl);a=max(a,clamp(pl,0.,1.));
  // shock ring for an entrance burst
  if(boost>.01){float rr=length(q)/W;float sh=exp(-pow((rr-(1.6-boost*1.3))/.05,2.))*boost;col+=vec3(1.)*sh;a=max(a,sh);}
  // fade to nothing at the canvas edge so the layer never shows its rectangle
  vec2 eg=min(px,uRes-px);float fade=smoothstep(0.,W*.3,min(eg.x,eg.y));
  gl_FragColor=vec4(col*1.15,clamp(a*.55,0.,1.))*fade;
}`;

let L = null;   // the singleton

function init() {
  if (L) return L;
  L = { ok: false };
  if (typeof document === 'undefined') return L;
  const cv = document.createElement('canvas');
  cv.id = 'forgedFx';
  cv.setAttribute('aria-hidden', 'true');
  cv.style.cssText = 'position:fixed;left:0;top:0;pointer-events:none;z-index:40;display:none;visibility:visible';   // inline: screens that hide body > canvas (forge-calm) must not hide this one
  const gl = cv.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: false, powerPreference: 'low-power' });
  if (!gl) return L;
  const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.warn('[forged-fx]', gl.getShaderInfoLog(s)); return null; } return s; };
  const vs = sh(gl.VERTEX_SHADER, VS), fs = sh(gl.FRAGMENT_SHADER, FS);
  if (!vs || !fs) return L;
  const pr = gl.createProgram(); gl.attachShader(pr, vs); gl.attachShader(pr, fs); gl.linkProgram(pr);
  if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return L;
  gl.useProgram(pr);
  const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(pr, 'p'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  const u = {};
  for (const n of ['uRes', 'uRect', 'uT', 'uKind', 'uBand', 'uTier', 'uBoost', 'uQ', 'uLvl']) u[n] = gl.getUniformLocation(pr, n);
  const lowStart = /Android/i.test(navigator.userAgent) && (navigator.deviceMemory || 8) <= 4;
  Object.assign(L, { ok: true, cv, gl, u, host: null, d: null, raf: 0, t0: performance.now(), step: lowStart ? 2 : 0,
    slow: 0, frames: 0, boostAt: -1, boostDur: 1, z: 40, check: 0,
    reduced: typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches });
  document.body.appendChild(cv);
  return L;
}

const bandOf = (d) => ((d.tier || 1) >= 17 ? 2 : (d.tier || 1) >= 9 ? 1 : 0);

/** Put the shared layer on `host` (an element shaped like the card) for card `d`.
 *  opts.z: CSS z-index for the layer (default 40); opts.boost: play an entrance
 *  burst of this many seconds; opts.level: effect intensity (1 hero, 0.5 calm). Returns false when there is no WebGL. */
export function attachForgedFx(host, d, opts = {}) {
  const S = init();
  if (!S.ok || !host || !d || !d.forged) { detachForgedFx(); return false; }
  S.host = host; S.d = d; S.covered = false; S.check = 0;
  // a host inside a pointer-events:none overlay can't be hit-tested; trust it
  S.noHit = getComputedStyle(host).pointerEvents === 'none';
  // sit just above the screen the card lives in (its outermost stacking context)
  let zTop = 0;
  for (let x = host; x && x !== document.body; x = x.parentElement) {
    const z = parseInt(getComputedStyle(x).zIndex, 10);
    if (Number.isFinite(z) && x.parentElement === document.body) zTop = z;
  }
  S.z = Math.max(opts.z || 40, zTop + 1); S.cv.style.zIndex = String(S.z);
  S.lvl = opts.level || 1;   // 1 = hero (sheet, ceremony, cut-in); ~0.5 = a big card in a busy screen
  if (opts.boost) { S.boostAt = performance.now(); S.boostDur = opts.boost; }
  S.cv.style.display = 'block';
  if (!S.raf) S.raf = requestAnimationFrame(frame);
  return true;
}
/** Detach (only if still attached to `host`, when given). */
export function detachForgedFx(host) {
  if (!L || !L.ok) return;
  if (host && L.host !== host) return;
  L.host = null; L.d = null;
  L.cv.style.display = 'none';
  if (L.raf) { cancelAnimationFrame(L.raf); L.raf = 0; }
}
export function forgedFxBurst(seconds = 1.2) { if (L && L.ok) { L.boostAt = performance.now(); L.boostDur = seconds; } }
if (typeof window !== 'undefined') window.__nmForgedFx = () => forgedFxState();   // QA hook
export function forgedFxState() {
  return L && L.ok ? { attached: !!L.host, id: L.d && L.d.id, step: L.step, w: L.cv.width, h: L.cv.height, shown: L.cv.style.display !== 'none', covered: !!L.covered } : { attached: false, ok: false };
}

function visible(el) {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 8 || r.height < 8) return false;
  if (r.bottom < 0 || r.top > innerHeight || r.right < 0 || r.left > innerWidth) return false;
  return r;
}

function frame(now) {
  const S = L;
  S.raf = 0;
  if (!S.host) return;
  const r = visible(S.host);
  if (!r) { S.cv.style.display = 'none'; S.raf = requestAnimationFrame(frame); return; }
  // every ~12 frames: is the card actually on top? (a sheet may cover it)
  if (!S.noHit && ++S.check % 12 === 0) {
    const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height * 0.3);
    S.covered = !!(hit && hit !== S.host && !S.host.contains(hit) && !hit.contains(S.host));
  }
  if (S.covered) { S.cv.style.display = 'none'; S.raf = requestAnimationFrame(frame); return; }
  S.cv.style.display = 'block';
  const pad = r.width * PAD;
  const cw = r.width + pad * 2, ch = r.height + pad * 2;
  const dpr = Math.min(devicePixelRatio || 1, STEPS[S.step]);
  const W = Math.round(cw * dpr), H = Math.round(ch * dpr);
  if (S.cv.width !== W || S.cv.height !== H) { S.cv.width = W; S.cv.height = H; }
  S.cv.style.width = `${cw}px`; S.cv.style.height = `${ch}px`;
  S.cv.style.transform = `translate(${r.left - pad}px,${r.top - pad}px)`;
  const { gl, u } = S;
  gl.viewport(0, 0, W, H);
  gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
  const t = S.reduced ? 1.5 : (now - S.t0) / 1000;
  const bt = S.boostAt < 0 ? 1 : (now - S.boostAt) / 1000 / S.boostDur;
  const boost = bt >= 1 ? 0 : Math.sin(Math.PI * Math.min(1, bt)) * (1 - bt * 0.3);
  gl.uniform2f(u.uRes, W, H);
  gl.uniform4f(u.uRect, pad * dpr, pad * dpr, r.width * dpr, r.height * dpr);
  gl.uniform1f(u.uT, t);
  gl.uniform1f(u.uKind, S.d.forged === 'fusion' ? 1 : 0);
  gl.uniform1f(u.uBand, bandOf(S.d));
  gl.uniform1f(u.uTier, S.d.tier || 1);
  gl.uniform1f(u.uBoost, boost);
  gl.uniform1f(u.uLvl, S.lvl || 1);
  gl.uniform1f(u.uQ, S.step === 0 ? 1 : S.step === 1 ? 0.6 : 0.25);
  gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  // live shedding: sustained long frames step the layer down (this session only)
  const dt = S.last ? now - S.last : 16; S.last = now;
  if (dt > 26 && dt < 200) S.slow++; else if (S.slow > 0) S.slow -= 0.25;
  if (S.slow > 45 && S.step < STEPS.length - 1) { S.step++; S.slow = 0; }
  if (!S.reduced || S.frames++ < 2) S.raf = requestAnimationFrame(frame);
}
